import { useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useI18n } from "@/i18n";

// Detect default country from current i18n language
const LANG_TO_COUNTRY = { es: "es", en: "gb", zh: "cn", ko: "kr" };

/**
 * Phone field with international dial-code selector + country flags.
 * Backed by `react-phone-input-2`. The dropdown uses native search + flag icons.
 *
 * Props:
 *   value: string (E.164 without "+") — e.g. "34600000000"
 *   onChange: (value: string) => void  — full E.164 (with "+")
 *   testid?: string
 */
export default function PhoneField({ value, onChange, testid = "phone-field" }) {
  const { lang } = useI18n();
  const defaultCountry = LANG_TO_COUNTRY[lang] || "es";

  useEffect(() => {
    // Inject a tiny stylesheet once that adapts the bundled CSS to our dark gold theme.
    const id = "phone-input-keepsoul-theme";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.innerHTML = `
.ks-phone .form-control { width:100% !important; background:rgba(255,255,255,.04) !important; border:1px solid rgba(201,163,106,.25) !important; color:#E8C997 !important; height:44px !important; border-radius:6px !important; font-size:14px; padding-left:52px !important; }
.ks-phone .form-control::placeholder { color: rgba(232, 201, 151, .5) !important; }
[data-theme="light"] .ks-phone .form-control { background:#FFF !important; color:#3A332B !important; border-color:rgba(155,127,84,.35) !important; }
[data-theme="light"] .ks-phone .form-control::placeholder { color: rgba(58,51,43,.5) !important; }
[data-theme="light"] .ks-phone .flag-dropdown { background:#FFF !important; border-color:rgba(155,127,84,.35) !important; }
[data-theme="light"] .ks-phone .country-list { background:#FFF !important; border-color:rgba(155,127,84,.35) !important; }
[data-theme="light"] .ks-phone .country-list .country { color:#3A332B !important; }
.ks-phone .form-control:focus { outline:none !important; box-shadow:0 0 0 2px rgba(201,163,106,.35) !important; border-color:rgba(201,163,106,.6) !important; }
.ks-phone .flag-dropdown { background:rgba(255,255,255,.04) !important; border:1px solid rgba(201,163,106,.25) !important; border-right:none !important; border-radius:6px 0 0 6px !important; }
.ks-phone .selected-flag:hover, .ks-phone .selected-flag:focus { background:rgba(201,163,106,.12) !important; }
.ks-phone .country-list { background:#0d0c0b !important; border:1px solid rgba(201,163,106,.25) !important; border-radius:6px !important; max-height:240px !important; }
.ks-phone .country-list .country { color:#E8C997 !important; }
.ks-phone .country-list .country.highlight { background:rgba(201,163,106,.18) !important; }
.ks-phone .country-list .country:hover { background:rgba(201,163,106,.10) !important; }
.ks-phone .country-list .search { background:rgba(255,255,255,.04) !important; padding:8px 10px !important; }
.ks-phone .country-list .search-box { background:rgba(255,255,255,.04) !important; border:1px solid rgba(201,163,106,.25) !important; color:#E8C997 !important; }
.ks-phone .country-list .country .dial-code { color:#A1A1AA !important; }
.ks-phone .invalid-number { border-color:rgba(220,38,38,.5) !important; }
`;
    document.head.appendChild(s);
  }, []);

  return (
    <div className="ks-phone" data-testid={testid}>
      <PhoneInput
        country={defaultCountry}
        value={value || ""}
        onChange={(v) => onChange(v ? `+${v}` : "")}
        enableSearch
        disableSearchIcon={false}
        countryCodeEditable={true}
        inputProps={{ "data-testid": `${testid}-input`, autoComplete: "tel" }}
        placeholder="+27 82 123 4567"
      />
    </div>
  );
}
